import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { fmtFecha, getRelaciones } from "../services/egresosService";

const cellSx = { py: 0.25, px: 0.6, fontSize: 12 };
const num = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const fmt = (v) => num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const montoDe = (r) => (r.moneda === "Bolivianos" ? num(r.bolivianos) : num(r.dolares));

// Qué está relacionado a un movimiento del extracto (al hacer click en su débito o crédito): pagos de facturas, recibos de crédito, cheques
// y traspasos, cada grupo con su suma, y la comparación contra el monto del movimiento.
export default function MovimientoRelacionesDialog({ movimiento, tipo, onClose }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!movimiento) return undefined;
    let activo = true;
    setDatos(null);
    setError("");
    getRelaciones(movimiento.id)
      .then(({ data }) => { if (activo) setDatos(data); })
      .catch((e) => { if (activo) setError(e?.response?.data?.error || "No se pudieron cargar los registros relacionados."); });
    return () => { activo = false; };
  }, [movimiento]);

  const montoMov = movimiento ? num(tipo === "debito" ? movimiento.debito : movimiento.credito) : 0;
  const grupos = datos ? [
    {
      clave: "pagos", titulo: "Pagos de facturas", vacio: "Sin pagos de facturas relacionados.",
      columnas: ["Pago", "Factura", "Cliente", "Periodo", "Depósito", "Fecha", "Monto"],
      filas: datos.pagos.map((p) => ({
        id: p.cod, monto: num(p.monto),
        celdas: [p.cod, p.num_fac ? `N° ${p.num_fac}` : p.id_factura, p.cliente || "-", p.mes_factura ? `${p.mes_factura} ${p.year_factura}` : "-", p.cod_banco || p.banco || "-", fmtFecha(p.fecha)],
      })),
    },
    {
      clave: "recibos", titulo: "Recibos de crédito", vacio: "",
      columnas: ["Recibo", "Comprobante", "Banco destino", "Fecha", "Monto"],
      filas: datos.recibos.map((r) => ({ id: r.id, monto: montoDe(r), celdas: [r.id, r.comprobante || "-", r.banco_destino || "-", fmtFecha(r.fecha)] })),
    },
    {
      clave: "egresos", titulo: "Cheques / egresos", vacio: "",
      columnas: ["Egreso", "N° cheque", "Banco", "Fecha", "Monto"],
      filas: datos.egresos.map((e) => ({ id: e.id, monto: montoDe(e), celdas: [e.id, e.nocheque || "-", e.banco || "-", fmtFecha(e.fecha)] })),
    },
    {
      clave: "traspasos", titulo: "Traspasos", vacio: "",
      columnas: ["Traspaso", "Origen", "Destino", "Fecha", "Monto"],
      filas: datos.traspasos.map((t) => ({ id: t.id, monto: montoDe(t), celdas: [t.id, t.banco_origen || "-", t.banco_destino || "-", fmtFecha(t.fecha)] })),
    },
  ].filter((g) => g.filas.length || g.clave === "pagos") : [];

  const total = grupos.reduce((s, g) => s + g.filas.reduce((a, f) => a + f.monto, 0), 0);
  const diferencia = Math.round((montoMov - total) * 100) / 100;

  return (
    <Dialog open={!!movimiento} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>
        {tipo === "debito" ? "Débito" : "Crédito"} {fmt(montoMov)} · movimiento {movimiento?.id}
        <Typography component="div" variant="caption" color="text.secondary">
          {movimiento ? `${fmtFecha(movimiento.fecha)} · ${movimiento.banco || ""} ${movimiento.cod_transaccion ? `· ${movimiento.cod_transaccion}` : ""}` : ""}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!datos && !error && <Box sx={{ p: 3, display: "grid", placeItems: "center" }}><CircularProgress size={26} /></Box>}
        {datos && (
          <Stack spacing={1.5}>
            {grupos.map((g) => {
              const subtotal = g.filas.reduce((a, f) => a + f.monto, 0);
              return (
                <Paper key={g.clave} variant="outlined" sx={{ p: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{g.titulo}</Typography>
                    <Chip size="small" label={g.filas.length} sx={{ height: 18, fontSize: 11, fontWeight: 700 }} />
                    <Box sx={{ flex: 1 }} />
                    {g.filas.length > 0 && <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Suma: {fmt(subtotal)}</Typography>}
                  </Stack>
                  {g.filas.length ? (
                    <TableContainer sx={{ maxHeight: 280 }}>
                      <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx }}>
                        <TableHead>
                          <TableRow>
                            {g.columnas.map((c, i) => <TableCell key={c} sx={{ fontWeight: 800 }} align={i === g.columnas.length - 1 ? "right" : "left"}>{c}</TableCell>)}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {g.filas.map((f) => (
                            <TableRow key={f.id} hover>
                              {f.celdas.map((c, i) => <TableCell key={i}>{c}</TableCell>)}
                              <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(f.monto)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">{g.vacio}</Typography>
                  )}
                </Paper>
              );
            })}

            <Paper variant="outlined" sx={{ p: 1.25, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Typography sx={{ fontSize: 13 }}>Monto del movimiento: <b>{fmt(montoMov)}</b></Typography>
              <Typography sx={{ fontSize: 13 }}>Total relacionado: <b>{fmt(total)}</b></Typography>
              <Chip size="small" color={diferencia === 0 ? "success" : "warning"} label={diferencia === 0 ? "Cuadra" : `Diferencia: ${fmt(diferencia)}`} sx={{ fontWeight: 700 }} />
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}
